import { Injectable, inject } from '@angular/core';
import { from, Observable, Subject, throwError } from 'rxjs';
import { HttpClient, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { MarketingOrderService } from '@lc/core/lib/services/marketing-order.service';
import { ApiService, PhotoApiService } from './api.service';
import { UploadPhoto } from '../models/upload-photo.model';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private http = inject(HttpClient);
  private apiService = inject(ApiService);
  private photosApiService = inject(PhotoApiService);
  private readonly userService = inject(UserService);
  private readonly marketingOrderService = inject(MarketingOrderService);

  private readonly resource = 'presign';
  userId: string;

  constructor() {
    this.userId = this.userService.getUserId();
  }

  private formatErrors(error: any) {
    return throwError(error.error);
  }

  public getPresignedUrl<T = any>(type: string, contentType?: string, extraParams?: string): Observable<T> {
    let url = `${this.resource}/${type}`;
    const params: string[] = [];
    if (contentType) {
      params.push(`contentType=${contentType}`);
    }
    if (extraParams) {
      params.push(extraParams);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    return this.apiService.get(url).pipe(
      map((result) => result),
    );
  }

  public upload(url, data) {
    return this.http.put(url, data).pipe(catchError(this.formatErrors));
  }

  public uploadWithProgress(url: string, data: any, progress: Subject<number>) {
    const req = new HttpRequest('PUT', url, data, { reportProgress: true });
    return this.http.request(req).pipe(
      tap((event) => {
        // On the UploadProgress event, update the progress status
        if (progress && event.type === HttpEventType.UploadProgress) {
          const percentDone = Math.round((100 * event.loaded) / event.total);
          progress?.next(percentDone);
        }
      }),
      // Filter to only the HttpResponse
      filter((event) => event instanceof HttpResponse),
      tap(() => progress?.complete()),
      catchError(this.formatErrors),
    );
  }

  public uploadCropData(cropData) {
    return this.photosApiService.post('upload/cropData', cropData);
  }

  public getPresignedUrlForAll(fileArray) {
    return this.photosApiService.post('presign/all', fileArray);
  }

  public getPresignedUrlForProfile(fileArray) {
    const endpoint = this.userId ? `presign/profile/${this.userId}` : 'presign/profile';
    return this.photosApiService.post(endpoint, fileArray);
  }

  public postImageUploadAndGetThumbnails(fileObject) {
    return this.photosApiService.post<UploadPhoto[]>('upload/thumbnails', fileObject).pipe(
      map((thumbnails) => thumbnails.map((thumb) => new UploadPhoto(thumb))),
    );
  }

  public deleteFileFromBucket(marketingOrderId: string) {
    return this.apiService.delete(`marketing-orders/${marketingOrderId}/media/youtubeVideoAd`).pipe(
      switchMap(() => from(this.marketingOrderService.refreshOrder(marketingOrderId))),
    );
  }
}
